/* global InfiniteMarquee */
/* eslint-disable no-redeclare */
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
gsap.registerPlugin(ScrollTrigger)
import Lenis from '@studio-freight/lenis'
import SplitType from 'split-type'
import Swiper from 'swiper'
import 'swiper/swiper-bundle.css'
import Headroom from 'headroom.js'

// Initialize smooth scrolling
let lenis;
const initSmoothScrolling = () => {
  lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
  lenis.on("scroll", () => ScrollTrigger.update());
  gsap.ticker.add(e => { lenis.raf(1000 * e); });
  gsap.ticker.lagSmoothing(0);
};

// Dark Mode functionality
const DarkMode = {
  init() {
    this.elements = {
      darkIcon: document.getElementById("theme-toggle-dark-icon"),
      lightIcon: document.getElementById("theme-toggle-light-icon"),
      toggleBtn: document.getElementById("theme-toggle")
    };
    if (!this.elements.toggleBtn) {
      console.warn("Dark mode toggle element not found on this page.");
      return;
    }
    this.setInitialTheme();
    this.bindEvents();
  },
  setInitialTheme() {
    if (
      localStorage.getItem("color-theme") === "dark" ||
      (!("color-theme" in localStorage) && window.matchMedia("(prefers-color-scheme: light)").matches)
    ) {
      this.elements.lightIcon?.classList.remove("hidden");
      document.documentElement?.classList.add("dark");
    } else {
      this.elements.darkIcon?.classList.remove("hidden");
      document.documentElement?.classList.remove("dark");
    }
  },
  bindEvents() {
    this.elements.toggleBtn && this.elements.toggleBtn.addEventListener("click", () => this.toggleTheme());
  },
  toggleTheme() {
    this.elements.darkIcon.classList.toggle("hidden");
    this.elements.lightIcon.classList.toggle("hidden");
    const isDark = document.documentElement.classList.toggle("dark");
    localStorage.setItem("color-theme", isDark ? "dark" : "light");
  }
};

// Animations object
const Animations = {
  initHeroGradient() {
    const wrapper = document.getElementById("hero-gradient-wrapper"),
          gradient = document.getElementById("hero-gradient");
    if (wrapper && gradient) {
      gsap.to(wrapper, { scale: 0.6, repeat: -1, duration: 3, yoyo: true, ease: 'none' });
      gsap.to(gradient, { repeat: -1, duration: 3, rotation: 360, ease: 'none' });
    }
  },
  initPointerAnimation() {
    const pointer = document.querySelector(".pointer");
    if (pointer && window.innerWidth >= 1023) {
      ["mousemove", "mouseenter"].forEach(evt => {
        document.addEventListener(evt, event => {
          gsap.to(pointer, { duration: 0.8, left: event.clientX, top: event.clientY, ease: "expo.out" });
        });
      });
    }
  },
  initTextReveal() {
    const revealElems = document.querySelectorAll(".reveal-text");
    console.log("Found reveal-text elements:", revealElems);
  
    document.querySelectorAll(".reveal-text").forEach(elem => {
      // If original text not stored, store it
      if (!elem.dataset.originalText) {
        elem.dataset.originalText = elem.textContent.trim();
      } else {
        // Reset element to its original text if reinitializing
        elem.innerHTML = elem.dataset.originalText;
      }
      
      // Now reapply SplitType
      let split = new SplitType(elem, { types: "chars" });
      gsap.from(split.chars, {
        scrollTrigger: {
          trigger: elem,
          start: "top 34%",
          end: "top -10%",
          scrub: true,
          pinSpacing: true
        },
        opacity: 0.1,
        stagger: 5,
        ease: "back.out"
      });
    });
  },  
  initTextReveal2() {
    document.querySelectorAll(".reveal-text-2").forEach(elem => {
      let split = new SplitType(elem, { types: "chars" });
      gsap.from(split.chars, {
        scrollTrigger: {
          trigger: elem,
          start: "top 90%",
          end: "top 40%",
          scrub: true
        },
        opacity: 0.1,
        stagger: 5,
        ease: "back.out"
      });
    });
  },
  initHorizontalScroll() {
    let wrapper = document.querySelector(".service-wrapper");
    if (wrapper) {
      let calcX = () => -(wrapper.scrollWidth - window.innerWidth);
      let tween = gsap.to(wrapper, { x: calcX, duration: 3, ease: "none" });
      ScrollTrigger.create({
        trigger: ".service-section",
        start: "top 0%",
        end: () => "+=" + -1 * calcX(),
        pin: true,
        animation: tween,
        scrub: 1,
        invalidateOnRefresh: true
      });
    }
  },
  initCtaImageReveal() {
    const ctaImg = document.getElementById("cta-img");
    if (ctaImg) {
      gsap.from(ctaImg, {
        scale: 0,
        rotation: -45,
        duration: 2.1,
        ease: "elastic.out(1.4, 1.2)",
        scrollTrigger: {
          trigger: ctaImg,
          start: "top 70%",
          end: "top 30%",
          scrub: false
        }
      });
    }
  },
  initImageHorizontalScroll() {
    let container = document.getElementById("extra-large-image-container");
    if (container) {
      ScrollTrigger.addEventListener("refreshInit", () => {});
      gsap.to(container, {
        x: () => -(container.scrollWidth - document.documentElement.clientWidth),
        ease: "none",
        pin: true,
        scrollTrigger: {
          trigger: container,
          pin: false,
          start: "top 90%",
          end: () => "+=" + (container.scrollWidth - document.documentElement.clientWidth),
          scrub: 1,
          invalidateOnRefresh: false,
          anticipatePin: 1
        }
      });
      window.addEventListener("resize", () => { ScrollTrigger.refresh(); });
    }
  },
  initSectionTitles() {
    document.querySelectorAll(".text-appear").forEach(elem => {
      const lines = new SplitType(elem, { types: "lines" }).lines;
      lines.forEach(line => {
        let words = new SplitType(line, { types: "words" });
        gsap.from(words.words, {
          scrollTrigger: {
            trigger: elem,
            start: "top 50%",
            end: "top 30%",
            scrub: false
          },
          y: 120,
          rotation: 21,
          stagger: 0.02,
          duration: 0.7,
          ease: "power2.out"
        });
      });
    });
  },
  initSectionTitles2() {
    document.querySelectorAll(".text-appear-2").forEach(elem => {
      const lines = new SplitType(elem, { types: "lines" }).lines;
      lines.forEach(line => {
        let words = new SplitType(line, { types: "words" });
        gsap.from(words.words, {
          scrollTrigger: {
            trigger: elem,
            start: "top 90%",
            end: "top 30%",
            scrub: false
          },
          y: 120,
          rotation: 21,
          stagger: 0.02,
          duration: 0.7,
          ease: "power2.out"
        });
      });
    });
  },
  initRevealElements() {
    document.querySelectorAll(".reveal-me").forEach(elem => {
      gsap.from(elem, {
        scrollTrigger: {
          trigger: elem,
          start: "top 90%",
          end: "top 50%",
          scrub: false
        },
        opacity: 0,
        y: 95,
        rotation: 2,
        filter: "blur(10px)",
        duration: 0.9,
        stagger: 0.1,
        ease: "power2.out"
      });
    });
  },
  initZoomAnimation() {
    const imgs = document.querySelectorAll(".zoom-image");
    gsap.to(imgs, {
      scale: 3.2,
      ease: "expoScale",
      scrollTrigger: {
        trigger: imgs,
        start: "top 20%",
        end: "top -30%",
        pin: true,
        scrub: 1
      }
    });
  },
  initVideoAnimation() {
    const wrappers = document.querySelectorAll(".video-wrapper");
    gsap.to(wrappers, {
      scale: 1,
      scrollTrigger: {
        trigger: ".video-section",
        start: "top 80%",
        end: "top 0%",
        scrub: 1
      }
    });
  },
  initScaleSmallAnimation() {
    const imgs = document.querySelectorAll(".scale-small-img");
    gsap.to(imgs, {
      scale: 0.8,
      scrollTrigger: {
        trigger: imgs,
        start: "top 50%",
        end: "top 0%",
        scrub: 1,
        ease: "power4.inOut"
      }
    });
  },
  initScaleSmallAnimation2() {
    const imgs = document.querySelectorAll(".scale-hero-img");
    gsap.to(imgs, {
      scale: 0.94,
      duration: 0.8,
      scrollTrigger: {
        trigger: imgs,
        start: "top 7%",
        end: "top 0%",
        scrub: 1,
        ease: "power4.inOut"
      }
    });
  }
};

// Components object
const Components = {
  initCircleText(selector = ".text", n = 10.3) {
    const elem = document.querySelector(selector);
    if (elem) {
      const spanMaker = (char, index) => `<span style="transform:rotate(${index * n}deg)">${char}</span>`;
      requestAnimationFrame(() => {
        elem.innerHTML = Array.from(elem.textContent.trim()).map(spanMaker).join("");
      });
    }
  },
  initScrollingMarquee() {
    const marqueeInner = document.querySelector(".marquee-inner");
    if (marqueeInner) {
      let r = 0;
      const originalHTML = marqueeInner.innerHTML;
      marqueeInner.innerHTML = originalHTML + originalHTML + originalHTML;
      const width = marqueeInner.offsetWidth / 3;
      const tween = gsap.to(".marquee-inner", {
        x: -2 * width,
        duration: 30,
        ease: "none",
        repeat: -1,
        onRepeat: () => { gsap.set(".marquee-inner", { x: -width }); }
      });
      let frame;
      window.addEventListener("scroll", () => {
        if (frame) window.cancelAnimationFrame(frame);
        frame = window.requestAnimationFrame(() => {
          const newY = window.pageYOffset;
          if (Math.abs(newY - r) > 1) {
            tween.timeScale(newY > r ? 1 : -1);
            r = newY;
          }
        });
      });
      let resizeFrame;
      window.addEventListener("resize", () => {
        if (resizeFrame) window.cancelAnimationFrame(resizeFrame);
        resizeFrame = window.requestAnimationFrame(() => {
          const newWidth = marqueeInner.offsetWidth / 3;
          tween.vars.x = -2 * newWidth;
          gsap.set(".marquee-inner", { x: -newWidth });
          tween.invalidate().restart();
        });
      });
    }
  },
  initFAQ() {
    const items = document.querySelectorAll(".accordion-item"),
          itemsV4 = document.querySelectorAll(".accordion-itemV4"),
          itemsV5 = document.querySelectorAll(".accordion-itemV5");
    items.forEach(item => {
      const header = item.querySelector(".accordion-header");
      header.addEventListener("click", () => {
        const isOpen = header.classList.contains("open");
        items.forEach(i => {
          const hdr = i.querySelector(".accordion-header");
          const body = i.querySelector(".accordion-body");
          hdr.classList.remove("open", "active");
          body.style.height = "0";
          i.style.borderColor = "transparent";
          i.style.paddingBottom = "0";
        });
        if (!isOpen) {
          header.classList.add("open", "active");
          const body = item.querySelector(".accordion-body");
          body.style.height = body.scrollHeight + "px";
          header.style.border = "1px solid transparent";
          header.style.paddingBottom = "40px";
        }
      });
    });
    itemsV4.forEach((item, index) => {
      const header = item.querySelector(".accordion-headerV4");
      const body = item.querySelector(".accordion-bodyV4");
      item.setAttribute("data-active", "false");
      header.addEventListener("click", () => {
        const isActive = header.classList.toggle("open");
        itemsV4.forEach((other, i) => {
          if (i !== index) {
            const otherHeader = other.querySelector(".accordion-headerV4");
            const otherBody = other.querySelector(".accordion-bodyV4");
            otherHeader.classList.remove("open", "active");
            otherBody.style.height = "0";
            otherBody.style.marginBottom = "0";
            other.setAttribute("data-active", "false");
          }
        });
        if (isActive) {
          body.style.height = body.scrollHeight + "px";
          header.classList.add("active");
          item.setAttribute("data-active", "true");
          body.style.marginBottom = "20px";
        } else {
          body.style.height = "0";
          header.classList.remove("active");
          item.setAttribute("data-active", "false");
          body.style.marginBottom = "0";
        }
      });
    });
    itemsV5.forEach(item => {
      const header = item.querySelector(".accordion-header");
      header.addEventListener("click", () => {
        const isOpen = header.classList.contains("open");
        itemsV5.forEach(i => {
          const hdr = i.querySelector(".accordion-header");
          const body = i.querySelector(".accordion-body");
          hdr.classList.remove("open", "active");
          body.style.height = "0";
          i.style.paddingBottom = "0";
        });
        if (!isOpen) {
          header.classList.add("open", "active");
          const body = item.querySelector(".accordion-body");
          body.style.height = body.scrollHeight + "px";
          header.style.paddingBottom = "40px";
        }
      });
    });
  },
  initCounter() {
    const counterElem = document.querySelector("#counter");
    if (counterElem) {
      const counters = document.querySelectorAll(".counter");
      let observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            counters.forEach(counter => {
              counter.innerText = "0";
              const updateCount = () => {
                const target = +counter.getAttribute("data-value");
                let count = +counter.innerText;
                const inc = Math.ceil(target / 100);
                if (count < target) {
                  counter.innerText = Math.min(count + inc, target);
                  setTimeout(updateCount, 20);
                }
              };
              updateCount();
              const parent = counter.parentElement;
              parent.style.opacity = "0";
              parent.style.transform = "translateY(20px)";
              setTimeout(() => {
                parent.style.transition = "all 0.7s ease";
                parent.style.opacity = "1";
                parent.style.transform = "translateY(0)";
              }, 200);
            });
            observer.unobserve(counterElem);
          }
        });
      }, { threshold: 0.17 });
      observer.observe(counterElem);
    }
  },
  initTeam() {
    const tabs = document.querySelectorAll(".tab-member");
    const detailsContainer = document.querySelector(".our-team-details");
    let processing = false;
    tabs.forEach(tab => {
      tab.addEventListener("click", () => {
        if (!tab.classList.contains("tab-active") && !processing) {
          const activeTab = document.querySelector(".tab-active");
          activeTab && activeTab.classList.remove("tab-active");
          tab.classList.add("tab-active");
          processing = true;
          detailsContainer.classList.add("transitioning");
          setTimeout(() => {
            const name = tab.querySelector("h3").textContent;
            const desc = tab.querySelector("p").textContent;
            const imgSrc = tab.querySelector("img").src;
            detailsContainer.querySelector("h2").textContent = name;
            detailsContainer.querySelector("p").textContent = desc;
            detailsContainer.querySelector("img").src = imgSrc;
            detailsContainer.offsetWidth;
            detailsContainer.classList.remove("transitioning");
            setTimeout(() => { processing = false; }, 400);
          }, 300);
        }
      });
    });
  }
};

// Menu Handler object
const MenuHandler = {
  init(el = document) {
    this.elements = {
      menu: el.querySelector(".menu"),
      overflow: el.querySelector(".menu-overflow"),
      items: el.querySelectorAll(".menu-list"),
      anchors: el.querySelectorAll(".menu-list-item"),
      dropdownAnchors: el.querySelectorAll(".menu-list-item-anchor"),
      openBtn: el.querySelector(".menu-open"),
      closeBtn: el.querySelector(".menu-close")
    };
    this.timeline = gsap.timeline({ paused: true, defaults: { ease: "custom", duration: 0.8 } });
    gsap.registerEase("custom", e => (e === 1 ? 1 : 1 - Math.pow(2, -10 * e)));
    this.setupInitialState();
    this.createTimeline();
    this.bindEvents();
  },
  destroy() {
    if (this.timeline) {
      this.timeline.kill();
    }
  },
  setupInitialState() {
    gsap.set(this.elements.menu, { pointerEvents: "none", autoAlpha: 0 });
    gsap.set(this.elements.overflow, { pointerEvents: "none", autoAlpha: 0, y: -30, rotate: -1, scale: 0.98 });
    gsap.set(this.elements.items, { autoAlpha: 0, y: -10, scale: 0.95 });
    gsap.set(this.elements.closeBtn, { autoAlpha: 0, y: -10, scale: 0.95 });
  },
  createTimeline() {
    this.timeline
      .to(this.elements.menu, { autoAlpha: 1, pointerEvents: "auto", duration: 0.5, ease: "power2.out" }, 0)
      .to(this.elements.overflow, { autoAlpha: 1, pointerEvents: "auto", y: 0, rotate: 0, scale: 1, duration: 0.6, ease: "custom" }, 0.1)
      .to(this.elements.items, { autoAlpha: 1, y: 0, scale: 1, stagger: { amount: 0.4, ease: "power2.out" }, duration: 0.7, ease: "custom" }, 0.2)
      .to(this.elements.closeBtn, { autoAlpha: 1, y: 0, scale: 1, duration: 0.5, ease: "back.out(1.7)" }, 0.3)
      .to(this.elements.openBtn, { autoAlpha: 0, y: -10, scale: 0.95, duration: 0.5, delay: 0.3, ease: "back.out(1.7)" }, 0.1)
      .to("body", { overflow: "hidden", pointerEvents: "none", duration: 0.1 }, 0);
  },
  bindEvents() {
    this.elements.openBtn && this.elements.openBtn.addEventListener("click", () => this.open());
    this.elements.closeBtn && this.elements.closeBtn.addEventListener("click", () => this.close());
    this.elements.dropdownAnchors.forEach(e => {
      e.addEventListener("click", () => this.handleDropdownClick(e));
    });
  },
  handleDropdownClick(e) {
    if (window.screen.width > 768) {
      this.elements.dropdownAnchors.forEach(a => a.classList.remove("active"));
      e.classList.add("active");
    } else {
      e.classList.toggle("active");
    }
  },
  open() {
    gsap.set(this.elements.overflow, { scale: 0.98 });
    this.elements.openBtn.classList.add("opacity-0");
    this.timeline.timeScale(1).play();
    this.elements.menu.style.pointerEvents = "auto";
  },
  close() {
    if (this.elements && this.elements.openBtn) {
      this.elements.openBtn.classList.remove("opacity-1");
      gsap.to(this.elements.openBtn, {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        duration: 0.5,
        delay: 0.5,
        ease: "back.out(1.7)"
      });
    } else {
      console.warn("MenuHandler.close: openBtn is not defined.");
    }
    
    if (this.timeline) {
      this.timeline.timeScale(1.2).reverse();
    }
    
    if (this.elements && this.elements.menu) {
      this.elements.menu.style.pointerEvents = "none";
    }
  }
   
};

// Initialize Swiper sliders
function initializeSliders() {
  new Swiper(".swiper", {
    loop: true,
    slidesPerView: 1,
    spaceBetween: 10,
    speed: 800,
    allowTouchMove: true,
    navigation: {
      nextEl: ".swiper-button-next",
      prevEl: ".swiper-button-prev"
    },
    breakpoints: {
      0: { slidesPerView: 1, spaceBetween: 10 },
      640: { slidesPerView: 2, spaceBetween: 20 },
      1024: { slidesPerView: 2, spaceBetween: 30 },
      1280: { slidesPerView: 4, spaceBetween: 24 }
    }
  });
  new Swiper(".user-swiper", {
    loop: true,
    slidesPerView: 1,
    spaceBetween: 4,
    speed: 1500,
    allowTouchMove: true,
    navigation: {
      nextEl: ".swiper-button-next",
      prevEl: ".swiper-button-prev"
    }
  });
}

// Function to reset overlay state (for Vue Router navigation)
const resetOverlay = () => {
  const overflow = document.querySelector(".menu-overflow");
  if (overflow) {
    gsap.set(overflow, { autoAlpha: 0, pointerEvents: "none", y: -30, rotate: -1, scale: 0.98 });
  }
  const menu = document.querySelector(".menu");
  if (menu) {
    menu.style.pointerEvents = "none";
  }
  // Also reset open button visibility if needed
  const openBtn = document.querySelector(".menu-open");
  if (openBtn) {
    gsap.set(openBtn, { autoAlpha: 1, y: 0, scale: 1 });
  }
};

// Wrap all initialization in a function to allow re-initialization on route change
const initApp = () => {
  initSmoothScrolling();
  DarkMode.init();
  MenuHandler.init();
  Components.initCircleText();
  Components.initScrollingMarquee();
  Components.initCounter();
  Components.initTeam();
  Animations.initHeroGradient();
  Animations.initPointerAnimation();
  //Animations.initTextReveal();
  //Animations.initTextReveal2();
  Animations.initHorizontalScroll();
  //Animations.initImageHorizontalScroll();
  //Animations.initCtaImageReveal();
  //Animations.initSectionTitles();
  //Animations.initSectionTitles2();
  //Animations.initRevealElements();
  //Animations.initZoomAnimation();
  //Animations.initVideoAnimation();
  //Animations.initScaleSmallAnimation();
  //Animations.initScaleSmallAnimation2();
  initializeSliders();

  // Pricing toggle for monthly/yearly options
/*   const toggle = document.getElementById("toggle");
  const monthlyOption = document.getElementById("monthlyOption");
  const yearlyOption = document.getElementById("yearlyOption");
  let isYearly = false;
  const monthlyCharge = document.getElementById("monthlyCharge");
  const yearlyCharge = document.getElementById("yearlyCharge");
  if (toggle) {
    monthlyOption.classList.add("active");
    toggle.addEventListener("click", () => {
      isYearly = !isYearly;
      toggle.classList.toggle("yearly");
      monthlyOption.classList.toggle("active");
      yearlyOption.classList.toggle("active");
      if (isYearly) {
        monthlyCharge.style.display = "none";
        yearlyCharge.style.display = "block";
      } else {
        monthlyCharge.style.display = "block";
        yearlyCharge.style.display = "none";
      }
    });
  } */

  // Initialize skew marquee if available
  const skewMarquee = document.getElementById("skew-Marquee");
  if (skewMarquee) {
    gsap.from(skewMarquee, {
      scrollTrigger: {
        trigger: skewMarquee,
        start: "top 80%",
        end: "top 50%",
        scrub: false
      },
      y: 200,
      skewX: "0deg",
      skewY: "0deg",
      rotation: 0,
      duration: 3
    });
  }

  // Initialize CTA inline slider
  const ctaSlider = document.querySelector(".cta-inline-slider");
  let sliderIndex = 0;
  if (ctaSlider) {
    setInterval(() => {
      sliderIndex = (sliderIndex + 1) % 3;
      ctaSlider.style.transform = `translateY(-${100 * sliderIndex}px)`;
    }, 3000);
  }

  // Initialize Headroom for header if available
  const headerElement = document.getElementById("header");
  if (headerElement) {
    const headroom = new Headroom(headerElement);
    headroom.init();
  }

  // Legacy InfiniteMarquee instances
  new InfiniteMarquee({
    element: ".marquee-container",
    speed: 120000,
    smoothEdges: true,
    direction: "left",
    pauseOnHover: true,
    gap: "30px",
    duplicateCount: 1,
    mobileSettings: { direction: "top", speed: 150000 },
    on: {
      beforeInit: () => { console.log("Not Yet Initialized"); },
      afterInit: () => { console.log("Initialized"); }
    }
  });
  new InfiniteMarquee({
    element: ".marquee-reverse-container",
    speed: 120000,
    smoothEdges: true,
    direction: "right",
    pauseOnHover: true,
    gap: "30px",
    duplicateCount: 1,
    mobileSettings: { direction: "right", speed: 150000 },
    on: {
      beforeInit: () => { console.log("Not Yet Initialized"); },
      afterInit: () => { console.log("Initialized"); }
    }
  });

  // Lenis scroll-to links
  document.querySelectorAll(".lenis-scroll-to").forEach(elem => {
    elem.addEventListener("click", () => {
      lenis.scrollTo(elem.getAttribute("href"), { offset: -100 });
    });
  });
};

// Initialize on window load
window.addEventListener("load", initApp);
// Expose initApp and resetOverlay globally for Vue Router navigation handling
window.initApp = initApp;
window.resetOverlay = resetOverlay;
// Also listen for a custom "routeChange" event to reset overlay (dispatch this event in your Vue Router afterEach hook)
window.addEventListener("routeChange", resetOverlay);

export { 
  DarkMode,
  initApp,
  resetOverlay,
  Animations,
  Components,
  MenuHandler
};
